import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useStore } from '../../shared/contexts/StoreContext';
import { useCart } from '../../shared/contexts/CartContext';
import { useComandaSession } from '../../shared/contexts/ComandaSessionContext';

import { Header } from './components/Header';
import { StatusBanner } from './components/StatusBanner';
import { CategoryNav } from './components/CategoryNav';
import { SearchBar } from './components/SearchBar';
import { ProductList } from './components/ProductList';
import { CartBar } from './components/CartBar';
import { CartModal } from '../../shared/components/CartModal';
import { ComandaViewer } from '../../shared/components/ComandaViewer';
import { useStatus } from '../../shared/contexts/StatusContext';

const Container = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding-bottom: 120px;

  @media (min-width: 1024px) {
    max-width: 1400px;
    padding-bottom: 80px;
  }
`;

const Content = styled.main`
  padding: 0 16px;
  margin-top: 12px;

  @media (min-width: 1024px) {
    padding: 0 40px;
    margin-top: 20px;
  }
`;

const Title = styled.h2`
  font-size: 12px;
  font-weight: 900;
  color: #9ca3af;
  text-transform: uppercase;
  margin-bottom: 12px;
  margin-left: 4px;
  letter-spacing: 0.1em;
`;

const CategoryContainer = styled.div`
  position: sticky;
  top: 0;
  background: var(--background, #f9fafb);
  z-index: 10;
  padding-bottom: 16px;

  @media (min-width: 1024px) {
    padding: 12px 0 16px;
  }
`;

const ProductsScrollContainer = styled.div`
  height: calc(100vh - 280px);
  overflow-y: auto;
  padding-bottom: 16px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }

  @media (min-width: 1024px) {
    height: auto;
    overflow-y: visible;
    padding-bottom: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 0 48px;
    align-items: flex-start;
  }
`;

const CategorySection = styled.section<{ $few?: boolean }>`
  margin-bottom: 16px;

  @media (min-width: 1024px) {
    margin-bottom: 48px;
    ${({ $few }) => $few
      ? `flex: 1; min-width: 300px;`
      : `width: 100%;`
    }
  }
`;

const CategorySectionTitle = styled.h3`
  display: none;

  @media (min-width: 1024px) {
    display: block;
    font-size: 13px;
    font-weight: 900;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 14px;
    padding-bottom: 8px;
    border-bottom: 2px solid #f3f4f6;
  }
`;

export const MenuPage: React.FC = () => {
    const { store, categories, products } = useStore();
    const { items, subtotal } = useCart();
    const { comandaMode, mesaNumber: comandaMesaNumber, cartaoCode, comandaId } = useComandaSession();
    const { isGestorMode } = useStatus();
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isCartOpen, setIsCartOpen] = useState(false);
    const categoryRefs = useRef<{ [key: string]: HTMLElement | null }>({});
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const filteredProducts = products.filter(product => {
        const searchLower = searchTerm.toLowerCase();
        return (
            product.name.toLowerCase().includes(searchLower) ||
            (product.desc && product.desc.toLowerCase().includes(searchLower))
        );
    });

    const filteredCategories = categories.filter(category => {
        const searchLower = searchTerm.toLowerCase();
        const hasMatchingProducts = filteredProducts.some(p => p.category === category.id);
        const categoryMatches = category.name.toLowerCase().includes(searchLower);
        return hasMatchingProducts || categoryMatches;
    });

    useEffect(() => {
        if (filteredCategories.length > 0 && (!selectedCategory || !filteredCategories.find(c => c.id === selectedCategory))) {
            setSelectedCategory(filteredCategories[0].id);
        }
    }, [filteredCategories, selectedCategory]);

    useEffect(() => {
        const isDesktop = window.innerWidth >= 1024;
        const root = isDesktop ? null : scrollContainerRef.current;

        if (!isDesktop && !scrollContainerRef.current) return;

        const observerOptions = {
            root,
            rootMargin: '-20% 0px -60% 0px',
            threshold: 0
        };

        const observerCallback = (entries: IntersectionObserverEntry[]) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const categoryId = entry.target.getAttribute('data-category-id');
                    if (categoryId) setSelectedCategory(categoryId);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);
        Object.values(categoryRefs.current).forEach(ref => {
            if (ref) observer.observe(ref);
        });

        return () => observer.disconnect();
    }, [categories, products]);

    const handleCategorySelect = (categoryId: string) => {
        setSelectedCategory(categoryId);
        const element = categoryRefs.current[categoryId];
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    if (!store) return null;

    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <Container>
            {!isGestorMode && <Header store={store} />}
            {!isGestorMode && <StatusBanner />}

            {comandaMode === 'cartao' && cartaoCode && (
                <ComandaViewer cartaoCode={cartaoCode} />
            )}
            {comandaMode === 'simples' && comandaId && (
                <ComandaViewer comandaId={comandaId} senha={sessionStorage.getItem('comanda_simples_senha') || ''} />
            )}

            <Content>
                <SearchBar value={searchTerm} onChange={setSearchTerm} />
                <Title>Categorias</Title>

                {filteredCategories.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        background: '#fff',
                        borderRadius: '20px',
                        marginBottom: '20px'
                    }}>
                        <p style={{ color: '#9ca3af', fontWeight: 'bold' }}>
                            {searchTerm ? 'Nenhum item encontrado' : 'Loja nova!'}
                        </p>
                        <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '8px' }}>
                            {searchTerm ? 'Tente buscar com outros termos.' : 'Em breve você verá aqui nossos produtos.'}
                        </p>
                    </div>
                ) : (
                    <>
                        <CategoryContainer>
                            <CategoryNav
                                categories={filteredCategories}
                                selectedCategory={selectedCategory}
                                onSelect={handleCategorySelect}
                            />
                        </CategoryContainer>

                        <ProductsScrollContainer ref={scrollContainerRef}>
                            {filteredCategories.map(category => {
                                const categoryProducts = filteredProducts.filter(p => p.category === category.id);
                                if (categoryProducts.length === 0) return null;
                                return (
                                    <CategorySection
                                        key={category.id}
                                        $few={categoryProducts.length <= 2}
                                        data-category-id={category.id}
                                        ref={(el) => { categoryRefs.current[category.id] = el; }}
                                    >
                                        <CategorySectionTitle>{category.name}</CategorySectionTitle>
                                        <ProductList products={categoryProducts} />
                                    </CategorySection>
                                );
                            })}
                        </ProductsScrollContainer>
                    </>
                )}
            </Content>

            {items.length > 0 && (
                <CartBar
                    total={subtotal}
                    count={totalItems}
                    onOpen={() => setIsCartOpen(true)}
                />
            )}

            <CartModal
                isOpen={isCartOpen}
                onClose={() => setIsCartOpen(false)}
            />
        </Container>
    );
};
